export type CoverageRow = {
  id: string;
  account_id: string;
  account_name: string;
  relationship_id?: string | null;
  owner_id?: string | null;
  owner_name?: string | null;
  owner_role?: string | null;
  on_roster?: boolean;
  contact_id: string;
  contact_name: string;
  contact_role: string;
  contact_role_label: string;
  classified?: boolean;
  lifecycle_state: string;
  status: string;
  rl?: number | null;
  posture?: string | null;
  pending_confirm?: boolean;
  goal_target_rl?: number | null;
  goal_status?: string | null;
  dormant_at?: string | null;
  gap_age_days?: number | null;
  fan_in: number;
  single_owner?: boolean;
  at_risk?: boolean;
  current_owner_ids?: string[];
  current_owner_names?: string[];
  resolution_route: string;
};

export type AttentionItem = {
  id: string;
  kind: string;
  account_id?: string | null;
  account_name: string;
  contact_id?: string | null;
  contact_name?: string | null;
  contact_role?: string | null;
  contact_role_label?: string | null;
  owner_id?: string | null;
  owner_name?: string | null;
  owner_role?: string | null;
  on_roster?: boolean;
  former_owner_id?: string | null;
  former_owner_name?: string | null;
  lifecycle_state?: string | null;
  status: string;
  condition_key: string;
  condition_label: string;
  gap_age_days?: number | null;
  gap_age_bucket?: string | null;
  has_history_context?: boolean | null;
  fan_in?: number | null;
  fan_in_band?: string | null;
  rl?: number | null;
  goal_target_rl?: number | null;
  current_owner_ids?: string[];
  current_owner_names?: string[];
  local_fan_out?: number | null;
  local_capacity?: number | null;
  global_fan_out?: number | null;
  global_capacity?: number | null;
  local_overload?: boolean;
  global_overload?: boolean;
  resolution_route: string;
  /** Setup / not-set-up attention extras. */
  setup_reasons?: string[];
  setup_contacts?: number | null;
  setup_team_size?: number | null;
};

export type OwnerCapacity = {
  owner_id: string;
  name?: string | null;
  role?: string | null;
  on_roster?: boolean;
  grade?: number | null;
  local_capacity: number;
  global_capacity: number;
};

/** Org-wide capacity settings by grade (from portfolio-coverage / Settings). */
export type GradeCapacity = {
  grade: number;
  local_capacity: number;
  global_capacity: number;
};

export type CoverageAccount = {
  account_id: string;
  name: string;
  tier?: string | null;
  segment?: string | null;
  /** Approved account-team member count (ATM roster). */
  team_size?: number;
};

export type CoverageFilters = {
  account: string;
  status: string;
  role: string;
  owner: string;
};

export type AnalysisFilters = {
  condition: string;
  age: string;
  history: string;
  capacity: string;
  fanIn: string;
};

export const EMPTY_FILTERS: CoverageFilters = {
  account: "all",
  status: "all",
  role: "all",
  owner: "all",
};

export const EMPTY_ANALYSIS: AnalysisFilters = {
  condition: "all",
  age: "all",
  history: "all",
  capacity: "all",
  fanIn: "all",
};

/** Contacts at or above this distinct-owner count are the fan-in band (prototype default 3). */
export const FAN_IN_THRESHOLD = 3;

export const STATUS_LABELS: Record<string, string> = {
  active: "Active",
  /** Resilience · two current owners, below the fan-in threshold. */
  "two-owners": "Two owners",
  "fan-in": "3+ owners",
  "open-orphaned": "Open — orphaned",
  "open-covered": "Open — covered elsewhere",
  transferred: "Transferred",
  dismissed: "Dismissed",
  pending: "Pending confirmation",
  "baseline-unmapped": "Baseline — unmapped",
  "single-owner": "Single-owner exposure",
  /** Relationship-strength composition bands (active pairs). */
  unassessed: "Not assessed",
  weak: "RL 0–1",
  moderate: "RL 2–3",
  strong: "RL 4–5",
  /** Account readiness composition / Not set up band. */
  "not-set-up": "Not set up",
  "no-contacts": "No contacts",
  "no-team": "No account team",
  "not-mapped": "Not mapped",
  "key-uncovered": "Key contact uncovered",
  strain: "Ownership strain",
  "single-key": "Single-threaded key roles",
  ready: "No coverage gaps",
  "other-unowned": "Other contacts unowned",
  setup: "Account not set up",
};

/**
 * Relationship-strength band keys (active owner–contact pairs).
 * Default list order: weakest non-empty first (RL 0–1 → Not assessed → …).
 */
export const EVIDENCE_BAND_KEYS = [
  "unassessed",
  "weak",
  "moderate",
  "strong",
] as const;

export type EvidenceBandKey = (typeof EVIDENCE_BAND_KEYS)[number];

/** Default strength-list band order (weakest first). */
export const STRENGTH_BAND_ORDER: EvidenceBandKey[] = [
  "weak",
  "unassessed",
  "moderate",
  "strong",
];

export function isEvidenceBandKey(key: string): key is EvidenceBandKey {
  return (EVIDENCE_BAND_KEYS as readonly string[]).includes(key);
}

/** True when an active coverage row falls in the Relationship-strength band. */
export function rowMatchesEvidenceBand(
  row: CoverageRow,
  band: EvidenceBandKey
): boolean {
  if (row.lifecycle_state !== "active") return false;
  if (band === "unassessed") return row.rl == null;
  if (row.rl == null) return false;
  if (band === "weak") return row.rl <= 1;
  if (band === "moderate") return row.rl >= 2 && row.rl <= 3;
  return row.rl >= 4;
}

export function evidenceBandLabel(band: EvidenceBandKey): string {
  return STATUS_LABELS[band] || band;
}

export const ATTENTION_CONDITIONS: Record<
  string,
  {
    label: string;
    priority: string;
    definition: string;
    impact: string;
    action: string;
  }
> = {
  lifecycle: {
    label: "Lifecycle gap",
    priority: "Urgent",
    definition:
      "A previously covered contact has lost an owner and still needs a resolved continuity path.",
    impact: "Relationship continuity and historical context can be lost.",
    action:
      "Assign or confirm a successor and review inherited relationship context.",
  },
  baseline: {
    label: "Baseline unmapped",
    priority: "Context-dependent",
    definition:
      "A known account contact has no mapped internal relationship owner and no predecessor handoff.",
    impact: "A strategically important stakeholder may be entirely uncovered.",
    action:
      "Confirm role importance, then assign an owner or explicitly mark the contact out of scope.",
  },
  pending: {
    label: "Pending confirmation",
    priority: "Time-sensitive",
    definition:
      "A successor exists, but inherited RL, goals, or relationship context have not been validated.",
    impact: "The team may act on stale or incorrect relationship information.",
    action:
      "Have the successor confirm or revise the inherited context promptly.",
  },
  "single-owner": {
    label: "Single-owner exposure",
    priority: "High for strategic roles",
    definition:
      "Only one internal person currently covers this customer contact.",
    impact: "One absence or departure can break relationship continuity.",
    action:
      "Add an appropriate secondary owner for strategically important contacts.",
  },
  capacity: {
    label: "Owner workload / fan-out",
    priority: "High / systemic",
    definition:
      "An owner carries more relationship assignments than the current role or grade recommendation.",
    impact:
      "Attention can become thin across several contacts or accounts, delaying follow-through.",
    action:
      "Prioritize relationships, redistribute ownership, or adjust staffing and capacity assumptions.",
  },
  "fan-in": {
    label: "3+ owner fan-in",
    priority: "Review",
    definition:
      "Three or more internal people currently cover the same customer contact.",
    impact:
      "Uncoordinated ownership can create duplicate outreach, unclear accountability, or customer fatigue.",
    action:
      "Name a lead owner and clarify supporting roles; three-plus ownership is not automatically bad.",
  },
  setup: {
    label: "Account not set up",
    priority: "Urgent",
    definition:
      "The account has no contacts or no account team, so coverage health cannot be measured yet.",
    impact: "Portfolio figures understate risk until the account is set up.",
    action:
      "Add contacts and assign an account team, then map owner–contact relationships.",
  },
};

/** Buying-committee key roles when MEDDPICC is on (prototype CW_KEY_ROLES). */
export const KEY_MEDDPICC_ROLES = ["EB", "DM", "CH"] as const;

/** Executive MEDDPICC code — used for Executive coverage when MEDDPICC is off. */
export const EXECUTIVE_ROLE = "EX";

export const READINESS_ORDER = [
  "no-contacts",
  "no-team",
  "not-mapped",
  "key-uncovered",
  "other-unowned",
  "strain",
  "single-key",
  "ready",
] as const;

export type ReadinessCategory = (typeof READINESS_ORDER)[number];

export const SETUP_CATEGORIES: ReadinessCategory[] = [
  "no-contacts",
  "no-team",
  "not-mapped",
];

export const SETUP_LABELS: Record<string, string> = {
  "no-contacts": "No contacts",
  "no-team": "No account team",
  "not-mapped": "Not mapped",
};

export type AccountReadiness = {
  accountId: string;
  category: ReadinessCategory;
  setup: boolean;
  reasons: string[];
  contacts: number;
  teamSize: number;
};

/** Heatmap "Why ▸" badge — readiness categories with a detail side panel (prototype CW_PANEL_READINESS). */
export const PANEL_READINESS_CATEGORIES: readonly string[] = [
  "key-uncovered",
  "other-unowned",
  "strain",
  "single-key",
];

/** True when status/role/owner (or non-readiness segment) narrows records — shows filter badge instead of Why. */
export function coverageRecordFilterActive(
  filters: CoverageFilters,
  readinessSegment?: string
): boolean {
  if (filters.status !== "all") return true;
  if (filters.role !== "all") return true;
  if (filters.owner !== "all") return true;
  if (
    readinessSegment &&
    readinessSegment !== "all" &&
    !PANEL_READINESS_CATEGORIES.includes(readinessSegment) &&
    readinessSegment !== "not-set-up" &&
    readinessSegment !== "setup" &&
    readinessSegment !== "ready"
  ) {
    return true;
  }
  return false;
}

export function isKeyMeddpiccRole(role: string | null | undefined): boolean {
  if (!role) return false;
  return (KEY_MEDDPICC_ROLES as readonly string[]).includes(role.toUpperCase());
}

export function isExecutiveContactRole(
  role: string | null | undefined
): boolean {
  if (!role) return false;
  const code = role.toUpperCase();
  return code === EXECUTIVE_ROLE || code === "EXECUTIVE";
}

/** Round percentage or null when denominator is empty (UI shows —). */
export function pctOrNull(
  numerator: number,
  denominator: number
): number | null {
  if (!denominator) return null;
  return Math.round((numerator / denominator) * 100);
}

type LensDef = [string, string, string, string];

export function ownerCountLabels(threshold = FAN_IN_THRESHOLD): {
  zero: string;
  single: string;
  two: string;
  fanIn: string;
} {
  return {
    zero: "No owner",
    single: "Single owner",
    two: threshold === 3 ? "Two owners" : "2–" + (threshold - 1) + " owners",
    fanIn: threshold + "+ owners",
  };
}

/**
 * Each contact once, by distinct current owners. A pending successor counts.
 * Bands: 0 no owner, 1 single, 2 two (below threshold), 3 fan-in.
 */
export function ownerBands(
  rows: CoverageRow[],
  threshold = FAN_IN_THRESHOLD
): number[] {
  const byContact = new Map<string, Set<string>>();
  rows.forEach(function (row) {
    const key = row.account_id + ":" + row.contact_id;
    if (!byContact.has(key)) byContact.set(key, new Set());
    if (row.lifecycle_state === "active" && row.owner_id) {
      byContact.get(key)!.add(row.owner_id);
    }
  });
  const counts = [0, 0, 0, 0];
  byContact.forEach(function (owners) {
    const n = owners.size;
    const band = n === 0 ? 0 : n === 1 ? 1 : n < threshold ? 2 : 3;
    counts[band] += 1;
  });
  return counts;
}

export const COMPOSITION_LENSES: {
  id: string;
  label: string;
  question: string;
  unit: string;
  displayUnit: string;
  meaning: string;
  decision: string;
  action: string;
  defs: LensDef[];
}[] = [
  {
    id: "readiness",
    label: "Account readiness",
    question: "Is each account set up, and where are its key gaps?",
    unit: "accounts",
    displayUnit: "accounts",
    meaning:
      "Counts each account once, under its worst condition. Every contact needs at least one owner; filling every matrix cell is not the goal.",
    decision:
      "Set up accounts with no contacts or team first, then cover key contacts, then give every other contact an owner, then relieve ownership strain and single-threaded key roles.",
    action:
      "Select a category to open its gaps or risks in Needs Attention, narrowed to those accounts. No coverage gaps opens the Accounts heatmap.",
    defs: [
      [
        "not-set-up",
        "Not set up",
        "bg-red-500",
        "No contacts, no account team, or no owner–contact pairs yet",
      ],
      [
        "key-uncovered",
        "Key contact uncovered",
        "bg-red-500",
        "A key contact has no owner, or a contact is orphaned",
      ],
      [
        "other-unowned",
        "Other contacts unowned",
        "bg-amber-500",
        "No key-role gap, but a contact outside the key roles has no owner",
      ],
      [
        "strain",
        "Ownership strain",
        "bg-amber-500",
        "An owner over account capacity, or a contact at or above the fan-in threshold",
      ],
      [
        "single-key",
        "Single-threaded key roles",
        "bg-amber-500",
        "A key contact has exactly one owner",
      ],
      [
        "ready",
        "No coverage gaps",
        "bg-emerald-500",
        "Set up; every contact has an owner; no strain or single-threaded key roles",
      ],
    ],
  },
  {
    id: "resilience",
    label: "Resilience",
    question: "How dependent is each contact on one person?",
    unit: "contacts",
    displayUnit: "contacts",
    meaning:
      "Shows whether contacts are uncovered, single-threaded, covered by two owners, or covered by more owners than needed.",
    decision:
      "0-owner contacts need an assignment decision. 1-owner contacts need a secondary owner when the role is strategic. Two owners is the resilience target; at or above the fan-in threshold, check that every owner is needed.",
    action:
      "Select a band: No owner opens Gaps, Single owner opens Risks, Two owners opens the Accounts heatmap, and the fan-in band opens Risks. A pending successor counts as an owner.",
    defs: [
      [
        "baseline-unmapped",
        ownerCountLabels().zero,
        "bg-red-500",
        "Never mapped or currently uncovered",
      ],
      [
        "single-owner",
        ownerCountLabels().single,
        "bg-amber-500",
        "Single-threaded coverage",
      ],
      [
        "two-owners",
        ownerCountLabels().two,
        "bg-emerald-500",
        "Meets the two-owner resilience target",
      ],
      [
        "fan-in",
        ownerCountLabels().fanIn,
        "bg-amber-500",
        "At or above the fan-in threshold (" +
          FAN_IN_THRESHOLD +
          "); review for over-coverage",
      ],
    ],
  },
  {
    id: "evidence",
    label: "Relationship strength",
    question: "How strong are our current relationships?",
    unit: "relationships",
    displayUnit: "relationships",
    meaning:
      "Counts each current owner–contact relationship once, by its RL. Not assessed is kept separate so it is not treated as weak.",
    decision:
      "Not assessed means the strength is not known yet, not that it is weak. RL 0–1 is a weak relationship. Unmapped contacts are a coverage gap: see Resilience (No owner) and Gaps.",
    action: "Select a band to list its relationships on the Accounts page.",
    defs: [
      [
        "unassessed",
        "Not assessed",
        "bg-slate-400",
        "Mapped, RL not yet assessed",
      ],
      ["weak", "RL 0–1", "bg-red-400", "Assessed weak relationship"],
      ["moderate", "RL 2–3", "bg-amber-500", "Assessed moderate relationship"],
      ["strong", "RL 4–5", "bg-emerald-500", "Assessed strong relationship"],
    ],
  },
  {
    id: "continuity",
    label: "Continuity",
    question: "Is coverage stable through ownership changes?",
    unit: "relationship records",
    displayUnit: "relationships",
    meaning:
      "Compares intact current coverage with unresolved owner loss and pending handoffs.",
    decision:
      "Resolve orphans first, then confirm pending successors. Covered-elsewhere is a recorded loss that already has another owner.",
    action:
      "Select a state to open Needs Attention › Gaps (Lifecycle or Pending); Current opens the Accounts heatmap.",
    defs: [
      [
        "open-orphaned",
        "Orphaned",
        "bg-red-500",
        "Lost coverage; no current owner",
      ],
      [
        "open-covered",
        "Covered elsewhere",
        "bg-amber-500",
        "Former owner left; another owner remains",
      ],
      [
        "pending",
        "Pending",
        "bg-blue-500",
        "Successor assigned; context not confirmed",
      ],
      [
        "active",
        "Current",
        "bg-emerald-500",
        "Active relationship with no open handoff",
      ],
    ],
  },
];

/** Native title on the composition unit badge (prototype `.cw-band-unit`). */
export function compositionUnitTooltip(
  lensId: string,
  meddpiccOn: boolean
): string {
  if (lensId === "readiness") {
    return (
      "Each account counted once, under its worst condition. Key contacts: " +
      (meddpiccOn
        ? "MEDDPICC Buying Committee (EB, DM, CH)"
        : "every contact (MEDDPICC off, so key roles are unknown)") +
      "."
    );
  }
  if (lensId === "resilience") {
    return "Each contact counted once, by its number of current owners.";
  }
  if (lensId === "evidence") {
    return (
      "Each current owner–contact relationship counted once, by its pairwise RL. " +
      "Unmapped contacts have no relationship, so they are not in this lens."
    );
  }
  return "Each owner–contact relationship counted once.";
}

/** Native title on non-Overview Portfolio scope (records × accounts). */
export const PORTFOLIO_SCOPE_TOOLTIP =
  "Coverage records are every owner–contact row in the current filter. " +
  "Across N accounts counts only accounts that appear in those rows. " +
  "The Accounts heatmap lists every account (Set up and Not set up), " +
  "including accounts with no coverage rows.";

export function effectiveStatus(row: CoverageRow): string {
  return row.status === "active" && row.single_owner
    ? "single-owner"
    : row.status;
}

/**
 * Filled status pill classes (prototype `.cw-status` / `.cw-st-*`).
 * Non-clickable — never use outline/button chrome for these labels.
 */
export function coverageStatusPillClass(status: string): string {
  switch (status) {
    case "open-orphaned":
      return "bg-red-100 text-red-800";
    case "open-covered":
    case "single-owner":
      return "bg-amber-100 text-amber-800";
    case "pending":
      return "bg-blue-100 text-blue-700";
    case "active":
    case "transferred":
      return "bg-green-100 text-green-800";
    case "dismissed":
    case "baseline-unmapped":
    default:
      return "bg-slate-100 text-slate-600";
  }
}

/** Band chip border tint on Relationship Strength strips (prototype `.cw-str-seg`). */
export const STRENGTH_BAND_CHIP_BORDER: Record<EvidenceBandKey, string> = {
  unassessed: "border-slate-300",
  weak: "border-red-300",
  moderate: "border-amber-300",
  strong: "border-green-300",
};

/** Strip display order — Not assessed · RL 0–1 · RL 2–3 · RL 4–5. */
export const STRENGTH_BAND_STRIP_ORDER: EvidenceBandKey[] = [
  ...EVIDENCE_BAND_KEYS,
];

export function scopeRows(
  rows: CoverageRow[],
  filters: CoverageFilters
): CoverageRow[] {
  return rows.filter(
    row =>
      (filters.account === "all" || row.account_id === filters.account) &&
      (filters.role === "all" || row.contact_role === filters.role) &&
      (filters.owner === "all" || row.owner_id === filters.owner)
  );
}

export function filteredRows(
  rows: CoverageRow[],
  filters: CoverageFilters
): CoverageRow[] {
  return scopeRows(rows, filters).filter(row => {
    if (filters.status === "all") return true;
    if (isEvidenceBandKey(filters.status)) {
      return rowMatchesEvidenceBand(row, filters.status);
    }
    // Continuity · Current
    if (filters.status === "active") return row.status === "active";
    // Resilience · Two owners (below fan-in threshold)
    if (filters.status === "two-owners") {
      const n = row.fan_in ?? 0;
      return row.lifecycle_state === "active" && n >= 2 && n < FAN_IN_THRESHOLD;
    }
    if (filters.status === "fan-in") {
      return (
        row.lifecycle_state === "active" &&
        (row.fan_in ?? 0) >= FAN_IN_THRESHOLD
      );
    }
    return effectiveStatus(row) === filters.status;
  });
}

/**
 * Status filter key for composition drills that land on Accounts.
 * Continuity Current → active. Resilience Two owners → two-owners.
 */
export function compositionAccountsStatus(
  lensId: string,
  categoryKey: string
): string | undefined {
  if (lensId === "continuity" && categoryKey === "active") return "active";
  if (lensId === "resilience" && categoryKey === "two-owners")
    return "two-owners";
  if (lensId === "readiness") return undefined;
  if (STATUS_LABELS[categoryKey]) return categoryKey;
  return undefined;
}

export function gapRows(rows: CoverageRow[]): CoverageRow[] {
  return rows.filter(
    row =>
      row.lifecycle_state !== "active" ||
      row.status === "pending" ||
      Boolean(row.single_owner)
  );
}

export function pluralUnit(
  n: number,
  singular: string,
  pluralForm?: string
): string {
  return n === 1 ? singular : pluralForm || singular + "s";
}

export function coverageKpis(
  rows: CoverageRow[],
  options?: {
    accounts?: CoverageAccount[];
    readinessByAccount?: Record<string, AccountReadiness>;
    ownerCapacities?: OwnerCapacity[];
    meddpiccOn?: boolean;
    accountId?: string | null;
  }
) {
  const active = rows.filter(row => row.lifecycle_state === "active");
  const contacts = new Set(
    rows.map(row => `${row.account_id}:${row.contact_id}`)
  );
  const covered = new Set(
    active.map(row => `${row.account_id}:${row.contact_id}`)
  );
  const orphan = rows.filter(row => row.status === "open-orphaned");
  const singleKeys = new Set(
    active
      .filter(row => row.single_owner)
      .map(row => `${row.account_id}:${row.contact_id}`)
  );
  const singleAccountIds = new Set(
    Array.from(singleKeys).map(key => key.split(":")[0])
  );
  const meddpiccOn = options?.meddpiccOn !== false;
  let singleKeyRoleCount = 0;
  if (meddpiccOn) {
    singleKeyRoleCount = new Set(
      active
        .filter(row => row.single_owner && isKeyMeddpiccRole(row.contact_role))
        .map(row => `${row.account_id}:${row.contact_id}`)
    ).size;
  }
  const assessedRel = active.filter(row => row.rl != null).length;
  const unassessedRel = Math.max(0, active.length - assessedRel);
  const assessmentVisibility = pctOrNull(assessedRel, active.length);

  let accountsNotSetUp = 0;
  const setupBreakdown: Record<string, number> = {
    "no-contacts": 0,
    "no-team": 0,
    "not-mapped": 0,
  };
  if (options?.readinessByAccount) {
    Object.values(options.readinessByAccount).forEach(rd => {
      if (!rd.setup) return;
      accountsNotSetUp += 1;
      if (rd.category in setupBreakdown) {
        setupBreakdown[rd.category] += 1;
      }
    });
  } else if (options?.accounts) {
    options.accounts.forEach(account => {
      const accountRows = rows.filter(r => r.account_id === account.account_id);
      const rd = accountReadiness(account.account_id, accountRows, {
        teamSize: account.team_size ?? 0,
      });
      if (!rd.setup) return;
      accountsNotSetUp += 1;
      if (rd.category in setupBreakdown) {
        setupBreakdown[rd.category] += 1;
      }
    });
  }

  const ownerIds = Array.from(
    new Set(active.map(row => row.owner_id).filter(Boolean) as string[])
  );
  const capByOwner = new Map(
    (options?.ownerCapacities || []).map(cap => [cap.owner_id, cap])
  );
  const singleAccountId = options?.accountId || null;
  const ownersOverCapacity = ownerIds.filter(ownerId => {
    const cap = capByOwner.get(ownerId);
    if (!cap) return false;
    if (singleAccountId) {
      const local = active.filter(
        row => row.owner_id === ownerId && row.account_id === singleAccountId
      ).length;
      return local > cap.local_capacity;
    }
    const global = active.filter(row => row.owner_id === ownerId).length;
    if (global > cap.global_capacity) return true;
    const byAccount = new Map<string, number>();
    active.forEach(row => {
      if (row.owner_id !== ownerId) return;
      byAccount.set(row.account_id, (byAccount.get(row.account_id) || 0) + 1);
    });
    return Array.from(byAccount.values()).some(n => n > cap.local_capacity);
  });

  return {
    coverageRate: contacts.size
      ? Math.round((covered.size / contacts.size) * 100)
      : 0,
    /** Covered contacts in scope — used when an owner filter is set. */
    owned: covered.size,
    gaps: orphan.length,
    affectedAccounts: new Set(orphan.map(row => row.account_id)).size,
    single: singleKeys.size,
    singleAccounts: singleAccountIds.size,
    singleKeyRoleCount,
    pending: active.filter(row => row.status === "pending").length,
    transferred: rows.filter(row => row.status === "transferred").length,
    baseline: rows.filter(row => row.status === "baseline-unmapped").length,
    accountsNotSetUp,
    setupBreakdown,
    ownersOverCapacity: ownersOverCapacity.length,
    ownersOverCapacityIds: ownersOverCapacity,
    ownerCount: ownerIds.length,
    assessedRel,
    unassessedRel,
    assessmentVisibility,
    activePairs: active.length,
  };
}

export type AccountMetrics = {
  total: number;
  coveredCount: number;
  /** Contact-once: contacts with ≥1 assessed active pair (map Y / Strong·Mod·Weak). */
  assessedCount: number;
  strongCount: number;
  moderateCount: number;
  weakCount: number;
  unassessedCount: number;
  /** Relationship-grain: assessed active pairs ÷ all active pairs. */
  relAssessedCount: number;
  relTotal: number;
  classifiedTotal: number;
  classifiedCoveredCount: number;
  teamSize: number;
  execTotal: number;
  execCoveredCount: number;
  activePairs: number;
  fanIn3: number;
  /** Contacts with 2 owners, or 2–(threshold-1) when the fan-in threshold is not 3. */
  two: number;
  orphanedContacts: number;
  concentrationCount: number;
  concentrationOwnerId: string | null;
  overload: number;
  /** Percentages — null when denominator is empty (render as —). */
  complete: number | null;
  roles: number | null;
  executive: number | null;
  /** Assessment visibility: assessed pairs ÷ current pairs. */
  assessed: number | null;
  /** Strong RL4–5 ÷ covered (contact once by strongest RL). */
  strong: number | null;
  moderate: number | null;
  weak: number | null;
  concentration: number | null;
  single: number;
  gaps: number;
  pending: number;
  coveredElsewhere: number;
  baseline: number;
};

export type AccountMetricsOptions = {
  teamSize?: number;
  ownerCapacities?: OwnerCapacity[];
};

/**
 * Per-account health metrics. Band %, assessed %, and strong/moderate/weak
 * count each contact once by its strongest active-pair RL.
 */
export function accountMetrics(
  accountId: string,
  rows: CoverageRow[],
  options: AccountMetricsOptions = {}
): AccountMetrics {
  const accountRows = rows.filter(row => row.account_id === accountId);
  const active = accountRows.filter(row => row.lifecycle_state === "active");
  const contactIds = new Set(accountRows.map(row => row.contact_id));
  const covered = new Set(active.map(row => row.contact_id));

  const maxRl = new Map<string, number>();
  active.forEach(row => {
    if (row.rl == null) return;
    const prev = maxRl.get(row.contact_id);
    maxRl.set(row.contact_id, prev == null ? row.rl : Math.max(prev, row.rl));
  });
  const assessed = new Set(maxRl.keys());
  let strongCount = 0;
  let moderateCount = 0;
  let weakCount = 0;
  maxRl.forEach(rl => {
    if (rl >= 4) strongCount += 1;
    else if (rl >= 2) moderateCount += 1;
    else weakCount += 1;
  });

  const classified = new Set(
    accountRows.filter(row => row.classified).map(row => row.contact_id)
  );
  let classifiedCovered = 0;
  classified.forEach(id => {
    if (covered.has(id)) classifiedCovered += 1;
  });

  const ownersByContact = new Map<string, Set<string>>();
  const ownerCounts: Record<string, number> = {};
  active.forEach(row => {
    if (!row.owner_id) return;
    ownerCounts[row.owner_id] = (ownerCounts[row.owner_id] || 0) + 1;
    const set = ownersByContact.get(row.contact_id) || new Set<string>();
    set.add(row.owner_id);
    ownersByContact.set(row.contact_id, set);
  });

  const fanIn3 = Array.from(ownersByContact.values()).filter(
    owners => owners.size >= FAN_IN_THRESHOLD
  ).length;
  const two = Array.from(ownersByContact.values()).filter(
    owners => owners.size >= 2 && owners.size < FAN_IN_THRESHOLD
  ).length;

  const topOwner = Object.entries(ownerCounts).sort(function (a, b) {
    return b[1] - a[1] || a[0].localeCompare(b[0]);
  })[0];
  const concentrationCount = topOwner ? topOwner[1] : 0;

  const capByOwner = new Map(
    (options.ownerCapacities || []).map(cap => [
      cap.owner_id,
      cap.local_capacity,
    ])
  );
  const overload = Object.entries(ownerCounts).filter(function ([ownerId, n]) {
    const local = capByOwner.get(ownerId);
    return local != null && n > local;
  }).length;

  const execContacts = new Set(
    accountRows
      .filter(row => isExecutiveContactRole(row.contact_role))
      .map(row => row.contact_id)
  );
  let execCovered = 0;
  execContacts.forEach(id => {
    if (covered.has(id)) execCovered += 1;
  });

  const teamSize = options.teamSize ?? 0;
  const coveredSize = covered.size;
  const relAssessedCount = active.filter(row => row.rl != null).length;
  const relTotal = active.length;

  return {
    total: contactIds.size,
    coveredCount: coveredSize,
    assessedCount: assessed.size,
    strongCount,
    moderateCount,
    weakCount,
    unassessedCount: Math.max(0, coveredSize - assessed.size),
    relAssessedCount,
    relTotal,
    classifiedTotal: classified.size,
    classifiedCoveredCount: classifiedCovered,
    teamSize,
    execTotal: execContacts.size,
    execCoveredCount: execCovered,
    activePairs: active.length,
    fanIn3,
    two,
    orphanedContacts: new Set(
      accountRows
        .filter(row => row.status === "open-orphaned")
        .map(row => row.contact_id)
    ).size,
    concentrationCount,
    concentrationOwnerId: topOwner ? topOwner[0] : null,
    overload,
    complete: pctOrNull(coveredSize, contactIds.size),
    roles: pctOrNull(classifiedCovered, classified.size),
    executive: pctOrNull(execCovered, execContacts.size),
    assessed: pctOrNull(relAssessedCount, relTotal),
    strong: pctOrNull(strongCount, coveredSize),
    moderate: pctOrNull(moderateCount, coveredSize),
    weak: pctOrNull(weakCount, coveredSize),
    concentration: pctOrNull(concentrationCount, active.length),
    single: new Set(
      active.filter(row => row.single_owner).map(row => row.contact_id)
    ).size,
    gaps: accountRows.filter(row => row.status === "open-orphaned").length,
    pending: active.filter(row => row.status === "pending").length,
    coveredElsewhere: accountRows.filter(row => row.status === "open-covered")
      .length,
    baseline: accountRows.filter(row => row.status === "baseline-unmapped")
      .length,
  };
}

/** Readiness lens display key: merge setup categories into not-set-up. */
export function readinessLensKey(rd: AccountReadiness): string {
  return rd.setup ? "not-set-up" : rd.category;
}

/** Short setup breakdown for Overview tile subtext. */
export function setupSummaryText(
  readinessByAccount: Record<string, AccountReadiness>
): string {
  const counts: Record<string, number> = {
    "no-contacts": 0,
    "no-team": 0,
    "not-mapped": 0,
  };
  Object.values(readinessByAccount).forEach(rd => {
    if (rd.setup && rd.category in counts) counts[rd.category] += 1;
  });
  const parts: string[] = [];
  SETUP_CATEGORIES.forEach(key => {
    if (counts[key]) {
      parts.push(counts[key] + " " + (SETUP_LABELS[key] || key).toLowerCase());
    }
  });
  return parts.join(" · ");
}

export function accountReadiness(
  accountId: string,
  rows: CoverageRow[],
  options: {
    teamSize?: number;
    meddpiccOn?: boolean;
    ownerCapacities?: OwnerCapacity[];
    metrics?: AccountMetrics;
  } = {}
): AccountReadiness {
  const meddpiccOn = options.meddpiccOn !== false;
  const m =
    options.metrics ||
    accountMetrics(accountId, rows, {
      teamSize: options.teamSize,
      ownerCapacities: options.ownerCapacities,
    });
  const accountRows = rows.filter(row => row.account_id === accountId);
  const active = accountRows.filter(row => row.lifecycle_state === "active");

  const ownersByContact = new Map<string, Set<string>>();
  active.forEach(row => {
    if (!row.owner_id) return;
    const set = ownersByContact.get(row.contact_id) || new Set<string>();
    set.add(row.owner_id);
    ownersByContact.set(row.contact_id, set);
  });
  const ownerCount = (contactId: string) =>
    ownersByContact.get(contactId)?.size || 0;

  const contactsMeta = new Map<
    string,
    { id: string; name: string; role: string }
  >();
  accountRows.forEach(row => {
    if (!contactsMeta.has(row.contact_id)) {
      contactsMeta.set(row.contact_id, {
        id: row.contact_id,
        name: row.contact_name,
        role: row.contact_role,
      });
    }
  });
  const contacts = Array.from(contactsMeta.values());

  const keyContacts = meddpiccOn
    ? contacts.filter(c => isKeyMeddpiccRole(c.role))
    : contacts;
  const uncovered = keyContacts.filter(c => ownerCount(c.id) === 0);
  const single = keyContacts.filter(c => ownerCount(c.id) === 1);

  const orphanIds = new Set(
    accountRows
      .filter(row => row.status === "open-orphaned")
      .map(row => row.contact_id)
  );
  const uncoveredNotOrphaned = uncovered.filter(c => !orphanIds.has(c.id));
  const orphaned = contacts.filter(c => orphanIds.has(c.id));

  const fanIn3Contacts = contacts.filter(
    c => ownerCount(c.id) >= FAN_IN_THRESHOLD
  );

  const ownerCounts: Record<string, number> = {};
  active.forEach(row => {
    if (!row.owner_id) return;
    ownerCounts[row.owner_id] = (ownerCounts[row.owner_id] || 0) + 1;
  });
  const capByOwner = new Map(
    (options.ownerCapacities || []).map(cap => [
      cap.owner_id,
      cap.local_capacity,
    ])
  );
  const overloadOwners = Object.entries(ownerCounts).filter(function ([
    ownerId,
    n,
  ]) {
    const local = capByOwner.get(ownerId);
    return local != null && n > local;
  });

  const reasons: { key: ReadinessCategory; text: string }[] = [];
  if (!m.total) {
    reasons.push({ key: "no-contacts", text: "No contacts added" });
  }
  // Active mappings prove an internal team exists (exec / ATM / sponsors), even
  // when approved ATM roster is empty — e.g. Nordic with exec-team only.
  const hasTeam = m.teamSize > 0 || m.activePairs > 0;
  if (!hasTeam) {
    reasons.push({ key: "no-team", text: "No account team assigned" });
  }
  if (m.total && hasTeam && !m.activePairs) {
    reasons.push({
      key: "not-mapped",
      text: "Contacts and team exist, but no owner–contact pairs are mapped",
    });
  }
  if (uncoveredNotOrphaned.length) {
    const word = meddpiccOn ? "key-role contact" : "contact";
    const words = meddpiccOn ? "key-role contacts" : "contacts";
    reasons.push({
      key: "key-uncovered",
      text:
        uncoveredNotOrphaned.length +
        " " +
        (uncoveredNotOrphaned.length === 1 ? word : words) +
        " with no owner: " +
        uncoveredNotOrphaned.map(c => c.name).join(", "),
    });
  }
  if (orphaned.length) {
    reasons.push({
      key: "key-uncovered",
      text:
        orphaned.length +
        (orphaned.length === 1 ? " orphaned contact" : " orphaned contacts"),
    });
  }
  // Non-key contacts with no owner (MEDDPICC on only — when off, every contact is "key").
  if (meddpiccOn) {
    const otherUnowned = contacts.filter(function (c) {
      return !isKeyMeddpiccRole(c.role) && ownerCount(c.id) === 0;
    });
    if (otherUnowned.length) {
      reasons.push({
        key: "other-unowned",
        text:
          otherUnowned.length +
          (otherUnowned.length === 1
            ? " other contact with no owner: "
            : " other contacts with no owner: ") +
          otherUnowned.map(c => c.name).join(", "),
      });
    }
  }
  if (overloadOwners.length) {
    reasons.push({
      key: "strain",
      text:
        overloadOwners.length +
        (overloadOwners.length === 1
          ? " owner over account capacity"
          : " owners over account capacity"),
    });
  }
  if (fanIn3Contacts.length) {
    const word = meddpiccOn ? "key-role contact" : "contact";
    const words = meddpiccOn ? "key-role contacts" : "contacts";
    reasons.push({
      key: "strain",
      text:
        fanIn3Contacts.length +
        " " +
        (fanIn3Contacts.length === 1 ? word : words) +
        " with 3+ owners",
    });
  }
  if (single.length) {
    const word = meddpiccOn ? "key-role contact" : "contact";
    const words = meddpiccOn ? "key-role contacts" : "contacts";
    reasons.push({
      key: "single-key",
      text:
        single.length +
        " " +
        (single.length === 1 ? word : words) +
        " with one owner: " +
        single.map(c => c.name).join(", "),
    });
  }

  const category =
    READINESS_ORDER.find(k => reasons.some(r => r.key === k)) || "ready";

  return {
    accountId,
    category,
    setup: SETUP_CATEGORIES.includes(category),
    reasons: reasons.map(r => r.text),
    contacts: m.total,
    teamSize: m.teamSize,
  };
}

export function buildReadinessByAccount(
  accounts: CoverageAccount[],
  rows: CoverageRow[],
  options: {
    meddpiccOn?: boolean;
    ownerCapacities?: OwnerCapacity[];
  } = {}
): Record<string, AccountReadiness> {
  const out: Record<string, AccountReadiness> = {};
  accounts.forEach(account => {
    const accountRows = rows.filter(r => r.account_id === account.account_id);
    out[account.account_id] = accountReadiness(
      account.account_id,
      accountRows,
      {
        teamSize: account.team_size ?? 0,
        meddpiccOn: options.meddpiccOn,
        ownerCapacities: options.ownerCapacities,
      }
    );
  });
  return out;
}

export function quadrantLabel(
  complete: number | null,
  strong: number | null
): string {
  const c = complete ?? 0;
  const s = strong ?? 0;
  if (c < 50) return s >= 50 ? "Strong but narrow" : "Critical / exposed";
  return s >= 50 ? "Healthy coverage" : "Broad but weak";
}

/** First click on a heatmap header sorts toward the worst value (prototype CW_HEAT_WORST_DIR). */
export const HEAT_WORST_DIR: Record<string, "asc" | "desc"> = {
  complete: "asc",
  roster: "asc",
  roles: "asc",
  executive: "asc",
  assessed: "asc",
  strong: "asc",
  moderate: "desc",
  weak: "desc",
  single: "desc",
  two: "asc",
  fanIn3: "desc",
  concentration: "desc",
  matching: "desc",
};

export function heatClass(key: string, value: number | null): string {
  if (value == null) return "bg-slate-50 text-slate-500";
  if (key === "moderate") return "bg-slate-50 text-slate-700";
  if (["complete", "roles", "assessed", "strong", "executive"].includes(key)) {
    if (value >= 80) return "bg-emerald-50 text-emerald-800";
    if (value >= 50) return "bg-amber-50 text-amber-800";
    return "bg-red-50 text-red-800 font-bold";
  }
  if (key === "weak") {
    if (value === 0) return "bg-emerald-50 text-emerald-800";
    if (value <= 20) return "bg-amber-50 text-amber-800";
    return "bg-red-50 text-red-800 font-bold";
  }
  if (key === "concentration") {
    if (value <= 40) return "bg-emerald-50 text-emerald-800";
    if (value <= 60) return "bg-amber-50 text-amber-800";
    return "bg-red-50 text-red-800 font-bold";
  }
  if (key === "two") {
    return value > 0
      ? "bg-emerald-50 text-emerald-800"
      : "bg-slate-50 text-slate-600";
  }
  if (key === "fanIn3") {
    return value === 0
      ? "bg-emerald-50 text-emerald-800"
      : "bg-amber-50 text-amber-800";
  }
  if (value === 0) return "bg-emerald-50 text-emerald-800";
  if (value <= 2) return "bg-amber-50 text-amber-800";
  return "bg-red-50 text-red-800 font-bold";
}

/** Badge under a heatmap account name when a record filter is on. */
export function heatRecordBadgeLabel(
  status: string,
  rows: CoverageRow[]
): string {
  const n = rows.length;
  const plural = n === 1 ? "" : "s";
  if (status === "unassessed") {
    return n + " relationship" + plural + " not assessed";
  }
  if (isEvidenceBandKey(status)) {
    return (
      n + " " + (STATUS_LABELS[status] || status) + " relationship" + plural
    );
  }
  const contacts = new Set(rows.map(row => row.contact_id)).size;
  return contacts + " contact" + (contacts === 1 ? "" : "s");
}

/** Contacts in an account with exactly 1, 2–threshold, or threshold+ current owners. */
export function accountContactIdsByOwnerBand(
  accountId: string,
  rows: CoverageRow[],
  band: "single" | "two" | "fanIn3"
): string[] {
  const owners = new Map<string, Set<string>>();
  rows.forEach(row => {
    if (
      row.account_id !== accountId ||
      row.lifecycle_state !== "active" ||
      !row.owner_id
    ) {
      return;
    }
    const set = owners.get(row.contact_id) || new Set<string>();
    set.add(row.owner_id);
    owners.set(row.contact_id, set);
  });
  const ids: string[] = [];
  owners.forEach((set, contactId) => {
    const n = set.size;
    const hit =
      band === "single"
        ? n === 1
        : band === "two"
          ? n >= 2 && n < FAN_IN_THRESHOLD
          : n >= FAN_IN_THRESHOLD;
    if (hit) ids.push(contactId);
  });
  return ids;
}

export function formatMetricPct(value: number | null): string {
  return value == null ? "\u2014" : value + "%";
}

export function compositionValues(
  lensId: string,
  defs: LensDef[],
  rows: CoverageRow[],
  readinessByAccount?: Record<string, AccountReadiness>
): number[] {
  if (lensId === "readiness" && readinessByAccount) {
    const list = Object.values(readinessByAccount);
    return defs.map(
      ([key]) => list.filter(rd => readinessLensKey(rd) === key).length
    );
  }
  if (lensId === "resilience") return ownerBands(rows);
  return defs.map(([key]) => {
    if (key === "unassessed") {
      return rows.filter(
        row => row.lifecycle_state === "active" && row.rl == null
      ).length;
    }
    if (key === "weak") {
      return rows.filter(
        row => row.lifecycle_state === "active" && row.rl != null && row.rl <= 1
      ).length;
    }
    if (key === "moderate") {
      return rows.filter(
        row =>
          row.lifecycle_state === "active" &&
          row.rl != null &&
          row.rl >= 2 &&
          row.rl <= 3
      ).length;
    }
    if (key === "strong") {
      return rows.filter(
        row => row.lifecycle_state === "active" && row.rl != null && row.rl >= 4
      ).length;
    }
    if (key === "active" && lensId === "continuity") {
      return rows.filter(
        row => row.lifecycle_state === "active" && row.status === "active"
      ).length;
    }
    return rows.filter(row => effectiveStatus(row) === key).length;
  });
}

/**
 * Lens-aware composition drill destination (prototype cwCompositionRoute).
 * Continuity Current and Resilience Two owners stay on Accounts; problem bands go to Needs Attention.
 */
export function compositionTargetView(
  lensId: string,
  categoryKey: string
): "attention" | "accounts" {
  return compositionNaView(lensId, categoryKey) ? "attention" : "accounts";
}

/** Which Needs Attention sub-view a composition category opens (null → Accounts). */
export function compositionNaView(
  lensId: string,
  categoryKey: string
): "gaps" | "risks" | null {
  const segment = lensId + ":" + categoryKey;
  // Mirror CW_COMPOSITION_NA_ROUTES — Continuity Current and Resilience Two owners stay on Accounts.
  if (
    segment === "continuity:open-orphaned" ||
    segment === "continuity:open-covered" ||
    segment === "continuity:pending" ||
    segment === "resilience:baseline-unmapped" ||
    segment === "readiness:not-set-up" ||
    segment === "readiness:key-uncovered" ||
    segment === "readiness:other-unowned"
  ) {
    return "gaps";
  }
  if (
    segment === "resilience:single-owner" ||
    segment === "resilience:fan-in" ||
    segment === "readiness:strain" ||
    segment === "readiness:single-key"
  ) {
    return "risks";
  }
  // Legacy readiness keys without lens prefix (defensive).
  if (lensId === "readiness") {
    if (
      categoryKey === "not-set-up" ||
      SETUP_CATEGORIES.includes(categoryKey as ReadinessCategory) ||
      categoryKey === "key-uncovered" ||
      categoryKey === "other-unowned" ||
      categoryKey === "not-mapped"
    ) {
      return "gaps";
    }
    if (categoryKey === "strain" || categoryKey === "single-key") {
      return "risks";
    }
  }
  return null;
}

/** Whether a composition drill should open Accounts in strength list mode. */
export function compositionOpensStrength(lensId: string): boolean {
  return lensId === "evidence";
}

/** Active pairs in an account matching a strength band (relationship grain). */
export function accountPairsByStrengthBand(
  accountId: string,
  rows: CoverageRow[],
  band: EvidenceBandKey
): CoverageRow[] {
  return rows.filter(
    row => row.account_id === accountId && rowMatchesEvidenceBand(row, band)
  );
}

/** Per-account Relationship-strength band counts (active pairs). */
export function accountStrengthBands(
  accountId: string,
  rows: CoverageRow[]
): Record<EvidenceBandKey, number> {
  return {
    unassessed: accountPairsByStrengthBand(accountId, rows, "unassessed")
      .length,
    weak: accountPairsByStrengthBand(accountId, rows, "weak").length,
    moderate: accountPairsByStrengthBand(accountId, rows, "moderate").length,
    strong: accountPairsByStrengthBand(accountId, rows, "strong").length,
  };
}

/** Contacts in an account whose strongest active RL falls in the band. */
export function accountContactsByStrongestBand(
  accountId: string,
  rows: CoverageRow[],
  band: EvidenceBandKey
): CoverageRow[] {
  const active = rows.filter(
    row => row.account_id === accountId && row.lifecycle_state === "active"
  );
  const maxRl = new Map<string, { rl: number | null; sample: CoverageRow }>();
  active.forEach(row => {
    const prev = maxRl.get(row.contact_id);
    if (!prev) {
      maxRl.set(row.contact_id, { rl: row.rl ?? null, sample: row });
      return;
    }
    if (row.rl == null) return;
    if (prev.rl == null || row.rl > prev.rl) {
      maxRl.set(row.contact_id, { rl: row.rl, sample: row });
    }
  });
  const out: CoverageRow[] = [];
  maxRl.forEach(({ rl, sample }) => {
    if (band === "unassessed") {
      if (rl == null) out.push(sample);
      return;
    }
    if (rl == null) return;
    if (band === "weak" && rl <= 1) out.push(sample);
    else if (band === "moderate" && rl >= 2 && rl <= 3) out.push(sample);
    else if (band === "strong" && rl >= 4) out.push(sample);
  });
  return out;
}

/** Per-account Relationship-evidence band counts (contact once by strongest RL). */
export function accountEvidenceBands(
  accountId: string,
  rows: CoverageRow[]
): Record<EvidenceBandKey, number> {
  return {
    unassessed: accountContactsByStrongestBand(accountId, rows, "unassessed")
      .length,
    weak: accountContactsByStrongestBand(accountId, rows, "weak").length,
    moderate: accountContactsByStrongestBand(accountId, rows, "moderate")
      .length,
    strong: accountContactsByStrongestBand(accountId, rows, "strong").length,
  };
}

export function alignmentFunnel(accountId: string, rows: CoverageRow[]) {
  const accountRows = rows.filter(row => row.account_id === accountId);
  const byContact = new Map<string, CoverageRow[]>();
  accountRows.forEach(row => {
    const list = byContact.get(row.contact_id) || [];
    list.push(row);
    byContact.set(row.contact_id, list);
  });
  let mapped = 0;
  let assessed = 0;
  let goaled = 0;
  let progressing = 0;
  let achieved = 0;
  byContact.forEach(list => {
    const active = list.filter(row => row.lifecycle_state === "active");
    if (active.length) mapped += 1;
    if (active.some(row => row.rl != null)) assessed += 1;
    if (active.some(row => row.goal_target_rl != null)) goaled += 1;
    if (active.some(row => row.goal_status === "in-progress")) progressing += 1;
    if (active.some(row => row.goal_status === "achieved")) achieved += 1;
  });
  return {
    total: byContact.size,
    mapped,
    assessed,
    goaled,
    progressing,
    achieved,
  };
}

export function sharedAttention(
  items: AttentionItem[],
  filters: CoverageFilters
): AttentionItem[] {
  return items.filter(
    item =>
      (filters.account === "all" || item.account_id === filters.account) &&
      (filters.status === "all" ||
        item.status === filters.status ||
        (filters.status === "active" && item.status === "active")) &&
      (filters.role === "all" || item.contact_role === filters.role) &&
      (filters.owner === "all" ||
        item.owner_id === filters.owner ||
        item.former_owner_id === filters.owner ||
        (item.current_owner_ids || []).includes(filters.owner))
  );
}

export function filteredAttention(
  items: AttentionItem[],
  filters: CoverageFilters,
  analysis: AnalysisFilters
): AttentionItem[] {
  return sharedAttention(items, filters).filter(item => {
    if (analysis.condition !== "all" && item.kind !== analysis.condition)
      return false;
    if (analysis.age !== "all" && item.gap_age_bucket !== analysis.age)
      return false;
    if (analysis.history !== "all") {
      if (item.has_history_context == null) return false;
      if ((analysis.history === "has") !== item.has_history_context)
        return false;
    }
    if (analysis.capacity !== "all") {
      if (item.kind !== "capacity") return false;
      if (analysis.capacity === "local" && !item.local_overload) return false;
      if (analysis.capacity === "global" && !item.global_overload) return false;
      if (
        analysis.capacity === "either" &&
        !item.local_overload &&
        !item.global_overload
      )
        return false;
    }
    if (analysis.fanIn !== "all" && item.fan_in_band !== analysis.fanIn)
      return false;
    return true;
  });
}

export function attentionGroupLabel(
  item: AttentionItem,
  groupBy: string
): string {
  if (groupBy === "condition")
    return ATTENTION_CONDITIONS[item.kind]?.label || item.condition_label;
  if (groupBy === "account") return item.account_name || "Portfolio-wide";
  if (groupBy === "formerOwner")
    return item.former_owner_name || "Not former-owner-specific";
  if (groupBy === "contactRole") {
    return item.contact_role_label || "Owner workload — not contact-specific";
  }
  return "All attention items";
}

export function groupAttention(
  items: AttentionItem[],
  groupBy: string
): { label: string; items: AttentionItem[] }[] {
  if (groupBy === "none") return [{ label: "All attention items", items }];
  const groups = new Map<string, AttentionItem[]>();
  items.forEach(item => {
    const label = attentionGroupLabel(item, groupBy);
    const list = groups.get(label) || [];
    list.push(item);
    groups.set(label, list);
  });
  return Array.from(groups.entries())
    .map(([label, groupItems]) => ({ label, items: groupItems }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function routeLabel(route: string): string {
  if (route === "team-views") return "Open in Team Views";
  if (route === "alignment") return "Confirm context";
  if (route === "history") return "View history";
  if (route === "owner") return "Open in Team Views";
  return "Open matrix";
}

/* ------------------------------------------------------------------ */
/*  Owners Summary | grid + hybrid portfolio filters (ed28eab)         */
/* ------------------------------------------------------------------ */

export type OwnersMode = "summary" | "grid";

export type OwnersPortfolioFilter =
  | "over"
  | "only"
  | "overCovered"
  | "unassessed"
  | "weak"
  | "moderate"
  | "strong";

export type OwnersSortKey =
  | "worst"
  | "name"
  | "accounts"
  | "load"
  | "over"
  | "highest"
  | "only"
  | "overCovered"
  | "unassessed";

export type OwnersSortState = { key: OwnersSortKey; dir: number };

export const DEFAULT_OWNERS_SORT: OwnersSortState = { key: "worst", dir: -1 };

export const OWNERS_PORTFOLIO_FILTER_KEYS: OwnersPortfolioFilter[] = [
  "over",
  "only",
  "overCovered",
  "unassessed",
  "weak",
  "moderate",
  "strong",
];

export function isOwnersPortfolioFilter(
  key: string | null | undefined
): key is OwnersPortfolioFilter {
  return (
    key != null &&
    (OWNERS_PORTFOLIO_FILTER_KEYS as readonly string[]).includes(key)
  );
}

/** Highlight column for the active portfolio filter. */
export const OWNERS_PORTFOLIO_HIT_COL: Record<
  OwnersPortfolioFilter,
  "load" | "only" | "unassessed" | "mix"
> = {
  over: "load",
  only: "only",
  overCovered: "only",
  unassessed: "unassessed",
  weak: "mix",
  moderate: "mix",
  strong: "mix",
};

export type OwnerPerAccountLoad = { id: string; load: number };

export type OwnerStats = {
  ownerId: string;
  name: string;
  role: string;
  onRoster: boolean;
  localCap: number | null;
  globalCap: number | null;
  departed: boolean;
  active: CoverageRow[];
  dormantOpen: CoverageRow[];
  accountIds: string[];
  perAccount: OwnerPerAccountLoad[];
  global: number;
  overGlobal: boolean;
  overCount: number;
  highest: OwnerPerAccountLoad | null;
  only: number;
  /** Active relationships on contacts at or above the fan-in threshold. */
  overCovered: number;
  unassessed: number;
  ratio: number;
  highRatio: number;
};

export type OwnerPortfolioAccountStats = {
  id: string;
  i: number;
  rows: CoverageRow[];
  load: number;
  over: boolean;
  only: number;
  overCovered: number;
  un: number;
  bands: number[];
};

function isOpenDormantRow(row: CoverageRow): boolean {
  return (
    row.lifecycle_state === "dormant" &&
    row.status !== "transferred" &&
    row.status !== "dismissed"
  );
}

function ownerFanOutLocal(
  ownerId: string,
  accountId: string,
  rows: CoverageRow[]
): number {
  let n = 0;
  rows.forEach(function (row) {
    if (
      row.lifecycle_state === "active" &&
      row.owner_id === ownerId &&
      row.account_id === accountId
    ) {
      n += 1;
    }
  });
  return n;
}

function ownerFanOutGlobal(ownerId: string, rows: CoverageRow[]): number {
  let n = 0;
  rows.forEach(function (row) {
    if (row.lifecycle_state === "active" && row.owner_id === ownerId) {
      n += 1;
    }
  });
  return n;
}

/** Per-owner rollups for Summary table and portfolio open. */
export function buildOwnerStats(
  ownerId: string,
  rows: CoverageRow[],
  capacity: OwnerCapacity | null | undefined,
  display?: { name?: string | null; role?: string | null; onRoster?: boolean }
): OwnerStats {
  const active = rows.filter(function (row) {
    return row.owner_id === ownerId && row.lifecycle_state === "active";
  });
  const dormantOpen = rows.filter(function (row) {
    return row.owner_id === ownerId && isOpenDormantRow(row);
  });
  const departed = active.length === 0;
  const list = departed ? dormantOpen : active;
  const seen = new Set<string>();
  const accountIds: string[] = [];
  list.forEach(function (row) {
    if (!seen.has(row.account_id)) {
      seen.add(row.account_id);
      accountIds.push(row.account_id);
    }
  });
  const localCap = capacity?.local_capacity ?? null;
  const globalCap = capacity?.global_capacity ?? null;
  const perAccount = accountIds.map(function (id) {
    return {
      id: id,
      load: departed
        ? list.filter(function (r) {
            return r.account_id === id;
          }).length
        : ownerFanOutLocal(ownerId, id, rows),
    };
  });
  const global = departed
    ? dormantOpen.length
    : ownerFanOutGlobal(ownerId, rows);
  const highest = departed
    ? null
    : perAccount.reduce<OwnerPerAccountLoad | null>(function (best, item) {
        if (!best || item.load > best.load) return item;
        return best;
      }, null);
  const overCount = departed
    ? 0
    : perAccount.filter(function (a) {
        return localCap != null && a.load > localCap;
      }).length;
  const only = active.filter(function (r) {
    return r.fan_in <= 1;
  }).length;
  const overCovered = active.filter(function (r) {
    return r.fan_in >= FAN_IN_THRESHOLD;
  }).length;
  const unassessed = active.filter(function (r) {
    return r.rl == null;
  }).length;
  const sample = list[0] || active[0] || dormantOpen[0];
  return {
    ownerId: ownerId,
    name:
      display?.name || capacity?.name || sample?.owner_name || "Unnamed owner",
    role: display?.role || capacity?.role || sample?.owner_role || "",
    onRoster: Boolean(
      display?.onRoster || capacity?.on_roster || sample?.on_roster
    ),
    localCap: localCap,
    globalCap: globalCap,
    departed: departed,
    active: active,
    dormantOpen: dormantOpen,
    accountIds: accountIds,
    perAccount: perAccount,
    global: global,
    overGlobal: !departed && globalCap != null && global > globalCap,
    overCount: overCount,
    highest: highest,
    only: only,
    overCovered: overCovered,
    unassessed: unassessed,
    ratio: globalCap ? global / globalCap : 0,
    highRatio: highest && localCap ? highest.load / localCap : 0,
  };
}

export function ownerWorstCompare(a: OwnerStats, b: OwnerStats): number {
  return (
    Number(a.departed) - Number(b.departed) ||
    Number(b.overGlobal) - Number(a.overGlobal) ||
    b.overCount - a.overCount ||
    b.ratio - a.ratio ||
    a.name.localeCompare(b.name)
  );
}

function ownerSortValue(s: OwnerStats, key: OwnersSortKey): number | string {
  if (key === "name") return s.name.toLowerCase();
  if (key === "accounts") return s.accountIds.length;
  if (key === "load") return s.ratio;
  if (key === "over") return s.overCount;
  if (key === "highest") return s.highRatio;
  if (key === "only") return s.only;
  if (key === "overCovered") return s.overCovered;
  if (key === "unassessed") return s.unassessed;
  return 0;
}

export function sortOwnerStats(
  stats: OwnerStats[],
  sort: OwnersSortState
): OwnerStats[] {
  const copy = stats.slice();
  if (sort.key === "worst") {
    return copy.sort(ownerWorstCompare);
  }
  const key = sort.key;
  const dir = sort.dir;
  return copy.sort(function (a, b) {
    if (a.departed !== b.departed)
      return Number(a.departed) - Number(b.departed);
    const va = ownerSortValue(a, key);
    const vb = ownerSortValue(b, key);
    const c =
      typeof va === "string" && typeof vb === "string"
        ? va.localeCompare(vb)
        : Number(va) - Number(vb);
    return c * dir || ownerWorstCompare(a, b);
  });
}

export function nextOwnersSort(
  current: OwnersSortState,
  key: OwnersSortKey
): OwnersSortState {
  if (key === "worst") return { ...DEFAULT_OWNERS_SORT };
  if (current.key === key) return { key: key, dir: -current.dir };
  return { key: key, dir: key === "name" ? 1 : -1 };
}

/** Band counts on an owner's relationship list (prototype CW_OWN_BANDS order). */
export function ownerRowBandCounts(rows: CoverageRow[]): number[] {
  return EVIDENCE_BAND_KEYS.map(function (band) {
    return rows.filter(function (r) {
      if (band === "unassessed") return r.rl == null;
      if (r.rl == null) return false;
      if (band === "weak") return r.rl <= 1;
      if (band === "moderate") return r.rl >= 2 && r.rl <= 3;
      return r.rl >= 4;
    }).length;
  });
}

export function buildOwnerPortfolioAccounts(
  stats: OwnerStats
): OwnerPortfolioAccountStats[] {
  const list = stats.departed ? stats.dormantOpen : stats.active;
  return stats.accountIds.map(function (id, i) {
    const accountRows = list.filter(function (r) {
      return r.account_id === id;
    });
    const load = stats.departed
      ? accountRows.length
      : ownerFanOutLocal(stats.ownerId, id, stats.active);
    return {
      id: id,
      i: i,
      rows: accountRows,
      load: load,
      over: !stats.departed && stats.localCap != null && load > stats.localCap,
      only: stats.departed
        ? 0
        : accountRows.filter(function (r) {
            return r.fan_in <= 1;
          }).length,
      overCovered: stats.departed
        ? 0
        : accountRows.filter(function (r) {
            return r.fan_in >= FAN_IN_THRESHOLD;
          }).length,
      un: accountRows.filter(function (r) {
        return r.rl == null;
      }).length,
      bands: ownerRowBandCounts(accountRows),
    };
  });
}

export function ownerPortfolioMatch(
  account: OwnerPortfolioAccountStats,
  filter: OwnersPortfolioFilter | null
): boolean {
  if (!filter) return true;
  if (filter === "over") return account.over;
  if (filter === "only") return account.only > 0;
  if (filter === "overCovered") return account.overCovered > 0;
  const bandIdx = EVIDENCE_BAND_KEYS.indexOf(filter as EvidenceBandKey);
  if (bandIdx >= 0) return account.bands[bandIdx] > 0;
  return true;
}

/** Contact/row match for only / RL bands (not used for `over`). */
export function ownerPortfolioRowMatch(
  row: CoverageRow,
  filter: OwnersPortfolioFilter | null
): boolean {
  if (!filter || filter === "over") return false;
  if (filter === "only") return row.fan_in <= 1;
  if (filter === "overCovered") return row.fan_in >= FAN_IN_THRESHOLD;
  if (filter === "unassessed") return row.rl == null;
  if (filter === "weak") return row.rl != null && row.rl <= 1;
  if (filter === "moderate")
    return row.rl != null && row.rl >= 2 && row.rl <= 3;
  if (filter === "strong") return row.rl != null && row.rl >= 4;
  return false;
}

export function ownerPortfolioUsesRowFilter(
  filter: OwnersPortfolioFilter | null
): boolean {
  return (
    filter === "only" ||
    filter === "overCovered" ||
    filter === "unassessed" ||
    filter === "weak" ||
    filter === "moderate" ||
    filter === "strong"
  );
}

export type OwnerPortfolioSortKey = "worst" | "name" | "load";

export function sortOwnerPortfolioAccounts(
  accounts: OwnerPortfolioAccountStats[],
  sort: OwnerPortfolioSortKey,
  accountName: (id: string) => string
): OwnerPortfolioAccountStats[] {
  const copy = accounts.slice();
  if (sort === "name") {
    return copy.sort(function (a, b) {
      return accountName(a.id).localeCompare(accountName(b.id));
    });
  }
  if (sort === "load") {
    return copy.sort(function (a, b) {
      return b.load - a.load || a.i - b.i;
    });
  }
  return copy.sort(function (a, b) {
    return (
      Number(b.over) - Number(a.over) ||
      b.load - a.load ||
      b.only - a.only ||
      b.un - a.un ||
      a.i - b.i
    );
  });
}

/** Default open set: filter or ≤5 → all visible; else over + focus. */
export function ownerPortfolioDefaultOpenIds(
  visible: OwnerPortfolioAccountStats[],
  filter: OwnersPortfolioFilter | null,
  focusAccountId?: string | null
): Set<string> {
  if (filter || visible.length <= 5) {
    return new Set(
      visible.map(function (a) {
        return a.id;
      })
    );
  }
  return new Set(
    visible
      .filter(function (a) {
        return a.over || a.id === focusAccountId;
      })
      .map(function (a) {
        return a.id;
      })
  );
}

/* ------------------------------------------------------------------ */
/*  Needs Attention — Gaps | Risks (prototype 753e733 / aaddcb2)       */
/* ------------------------------------------------------------------ */

export type NaView = "gaps" | "risks";

export type GapType = "setup" | "lifecycle" | "pending" | "unowned";

export type RiskType = "single-owner" | "capacity" | "fan-in";

export const GAP_TYPES: {
  key: GapType;
  label: string;
  short: string;
  title: string;
  sub: string;
}[] = [
  {
    key: "setup",
    label: "Not set up",
    short: "Not set up",
    title: "Account not set up",
    sub: "Setup hides every other gap in the account",
  },
  {
    key: "lifecycle",
    label: "Lifecycle",
    short: "Lifecycle",
    title: "Lifecycle gap",
    sub: "Grouped by former owner",
  },
  {
    key: "pending",
    label: "Pending",
    short: "Pending",
    title: "Pending confirmation",
    sub: "Successor named, handoff not confirmed",
  },
  {
    key: "unowned",
    label: "Unowned",
    short: "Unowned",
    title: "Unowned",
    sub: "Every contact needs at least one owner",
  },
];

export const RISK_TYPES: RiskType[] = ["single-owner", "capacity", "fan-in"];

export const GAP_TYPE_RANK: Record<GapType, number> = {
  setup: 0,
  lifecycle: 1,
  pending: 2,
  unowned: 3,
};

/** Open-for filter: minimum age, not an exclusive band. `min` null = any age. */
export const NA_AGE_FILTERS: {
  key: string;
  label: string;
  min: number | null;
}[] = [
  { key: "all", label: "Any age", min: null },
  { key: "over30", label: "Over 30 days", min: 30 },
  { key: "over60", label: "Over 60 days", min: 60 },
  { key: "over90", label: "Over 90 days", min: 90 },
];

export function naAgeMatch(
  item: { ageDays: number | null },
  age: string
): boolean {
  const spec =
    NA_AGE_FILTERS.find(function (f) {
      return f.key === age;
    }) || NA_AGE_FILTERS[0];
  if (spec.min == null) return true;
  return item.ageDays != null && item.ageDays > spec.min;
}

export type GapItem = AttentionItem & {
  gapType: GapType;
  isKey: boolean;
  keyBadge: string | null;
  needsTeam: boolean;
  accountIds: string[];
  ageDays: number | null;
  ageBand: string;
  foldedCount: number;
  foldedKeyCount: number;
};

export type RiskItem = AttentionItem & {
  riskType: RiskType;
  isKey: boolean;
  keyBadge: string | null;
  accountIds: string[];
};

/** MEDDPICC on: EB / DM / CH. Off: Executive contacts only. */
export function coverageKeyBadge(
  role: string | null | undefined,
  meddpiccOn: boolean
): string | null {
  if (meddpiccOn) {
    if (!role) return null;
    const code = role.toUpperCase();
    return (KEY_MEDDPICC_ROLES as readonly string[]).includes(code)
      ? code
      : null;
  }
  return isExecutiveContactRole(role) ? "Exec" : null;
}

function ageBandOf(days: number | null | undefined): string {
  if (days == null || !Number.isFinite(days)) return "unknown";
  if (days <= 30) return "0-30";
  if (days <= 60) return "31-60";
  if (days <= 90) return "61-90";
  return "90+";
}

export function buildGapItems(
  attentionItems: AttentionItem[],
  options: { meddpiccOn?: boolean } = {}
): GapItem[] {
  const meddpiccOn = options.meddpiccOn !== false;
  const base = attentionItems.filter(function (i) {
    return (
      i.kind === "setup" ||
      i.kind === "lifecycle" ||
      i.kind === "pending" ||
      i.kind === "baseline"
    );
  });
  const noTeamIds = new Set(
    base
      .filter(function (i) {
        return i.kind === "setup" && i.condition_key === "no-team";
      })
      .map(function (i) {
        return i.account_id;
      })
      .filter(Boolean) as string[]
  );
  const items: GapItem[] = base.map(function (i) {
    const gapType: GapType =
      i.kind === "baseline" ? "unowned" : (i.kind as GapType);
    const keyBadge =
      gapType === "setup" ? null : coverageKeyBadge(i.contact_role, meddpiccOn);
    const ageDays = i.gap_age_days ?? null;
    return {
      ...i,
      gapType: gapType,
      keyBadge: keyBadge,
      isKey: !!keyBadge,
      needsTeam:
        gapType === "unowned" && !!i.account_id && noTeamIds.has(i.account_id),
      accountIds: i.account_id ? [i.account_id] : [],
      ageDays: ageDays,
      ageBand: i.gap_age_bucket || ageBandOf(ageDays),
      foldedCount: 0,
      foldedKeyCount: 0,
    };
  });
  items.forEach(function (item) {
    if (item.gapType !== "setup" || !item.account_id) return;
    const folded = items.filter(function (other) {
      return (
        other.gapType === "unowned" && other.account_id === item.account_id
      );
    });
    item.foldedCount = folded.length;
    item.foldedKeyCount = folded.filter(function (c) {
      return c.isKey;
    }).length;
    item.isKey = item.foldedKeyCount > 0;
  });
  return items.sort(gapPriorityCompare);
}

export function gapPriorityCompare(a: GapItem, b: GapItem): number {
  return (
    GAP_TYPE_RANK[a.gapType] - GAP_TYPE_RANK[b.gapType] ||
    Number(b.isKey) - Number(a.isKey) ||
    (a.status === "open-orphaned" ? 0 : 1) -
      (b.status === "open-orphaned" ? 0 : 1) ||
    (a.account_name || "").localeCompare(b.account_name || "") ||
    (a.contact_name || "").localeCompare(b.contact_name || "")
  );
}

export function gapAgeCompare(a: GapItem, b: GapItem): number {
  return (
    (b.ageDays ?? -1) - (a.ageDays ?? -1) ||
    Number(b.isKey) - Number(a.isKey) ||
    gapPriorityCompare(a, b)
  );
}

export function buildRiskItems(
  attentionItems: AttentionItem[],
  options: { meddpiccOn?: boolean } = {}
): RiskItem[] {
  const meddpiccOn = options.meddpiccOn !== false;
  return attentionItems
    .filter(function (i) {
      return (
        i.kind === "single-owner" ||
        i.kind === "capacity" ||
        i.kind === "fan-in"
      );
    })
    .map(function (i) {
      const keyBadge = coverageKeyBadge(i.contact_role, meddpiccOn);
      return {
        ...i,
        riskType: i.kind as RiskType,
        keyBadge: keyBadge,
        isKey: !!keyBadge,
        accountIds: i.account_id ? [i.account_id] : [],
      };
    });
}

export function naOwnerMatch(
  item: GapItem | RiskItem,
  ownerId: string
): boolean {
  if ("gapType" in item && item.gapType) {
    return item.former_owner_id === ownerId || item.owner_id === ownerId;
  }
  const risk = item as RiskItem;
  return (
    risk.owner_id === ownerId ||
    (risk.current_owner_ids || []).includes(ownerId)
  );
}

export function naSharedMatch(
  item: GapItem | RiskItem,
  filters: CoverageFilters,
  options: { keyOnly?: boolean; readinessAccountIds?: Set<string> | null } = {}
): boolean {
  if (filters.account !== "all" && !item.accountIds.includes(filters.account)) {
    return false;
  }
  if (filters.role !== "all" && item.contact_role !== filters.role) {
    return false;
  }
  if (filters.owner !== "all" && !naOwnerMatch(item, filters.owner)) {
    return false;
  }
  if (options.keyOnly && !item.isKey) return false;
  if (
    options.readinessAccountIds &&
    options.readinessAccountIds.size > 0 &&
    !(
      item.accountIds.length &&
      item.accountIds.every(function (id) {
        return options.readinessAccountIds!.has(id);
      })
    )
  ) {
    return false;
  }
  return true;
}

export function riskTypeLabel(type: RiskType, fanInThreshold = 3): string {
  if (type === "single-owner") return "Single-owner exposure";
  if (type === "capacity") return "Owner workload";
  return fanInThreshold + "+ owner fan-in";
}

export function riskShortLabel(type: RiskType): string {
  if (type === "single-owner") return "Single-owner";
  if (type === "capacity") return "Workload";
  return "Fan-in";
}

export function gapTypeLabel(item: GapItem): string {
  if (item.gapType === "setup") {
    return (
      "Not set up · " + (SETUP_LABELS[item.condition_key] || item.condition_key)
    );
  }
  if (item.gapType === "lifecycle") {
    return item.status === "open-orphaned"
      ? "Lifecycle — orphaned"
      : "Lifecycle — covered elsewhere";
  }
  if (item.gapType === "pending") return "Pending confirmation";
  return item.isKey ? "Unowned · key role" : "Unowned";
}

export function gapWhoCovers(item: GapItem): string {
  if (item.gapType === "setup") {
    return item.setup_team_size
      ? "Team of " + item.setup_team_size + ", no contacts yet"
      : "Nobody — no account team";
  }
  if (item.gapType === "lifecycle") {
    if (item.status === "open-orphaned") {
      return (
        "Nobody — " + (item.former_owner_name || "former owner") + " (departed)"
      );
    }
    const names = (item.current_owner_names || []).join(", ") || "others";
    return (
      "Covered elsewhere by " +
      names +
      " · " +
      (item.former_owner_name || "former owner") +
      " departed"
    );
  }
  if (item.gapType === "pending") {
    return (item.owner_name || "Successor") + " (successor, not confirmed)";
  }
  if (item.needsTeam) return "Nobody — no account team";
  return "Nobody — never mapped";
}

export type RiskModel = {
  single: RiskItem[];
  capacity: RiskItem[];
  fan: RiskItem[];
  patterns: number;
  ownerLine: {
    id: string;
    name: string;
    types: number;
    single: RiskItem[];
    capacity: RiskItem[];
    fan: RiskItem[];
  }[];
  multi: {
    id: string;
    name: string;
    types: Set<string>;
    keyContacts: number;
    counts: Record<RiskType, number>;
    commonOwner: string;
  }[];
};

export function buildRiskModel(items: RiskItem[]): RiskModel {
  const single = items.filter(i => i.riskType === "single-owner");
  const capacity = items.filter(i => i.riskType === "capacity");
  const fan = items.filter(i => i.riskType === "fan-in");
  const owners = new Map<
    string,
    {
      id: string;
      name: string;
      single: RiskItem[];
      capacity: RiskItem[];
      fan: RiskItem[];
    }
  >();
  const add = function (
    id: string | null | undefined,
    name: string | null | undefined,
    key: "single" | "capacity" | "fan",
    item: RiskItem
  ) {
    if (!id) return;
    if (!owners.has(id)) {
      owners.set(id, {
        id: id,
        name: name || "Owner",
        single: [],
        capacity: [],
        fan: [],
      });
    }
    owners.get(id)![key].push(item);
  };
  single.forEach(i => add(i.owner_id, i.owner_name, "single", i));
  capacity.forEach(i => add(i.owner_id, i.owner_name, "capacity", i));
  fan.forEach(i => {
    (i.current_owner_ids || []).forEach(function (oid, idx) {
      add(oid, (i.current_owner_names || [])[idx], "fan", i);
    });
  });
  const ownerLine = Array.from(owners.values())
    .map(function (o) {
      const types = (["single", "capacity", "fan"] as const).filter(
        k => o[k].length
      ).length;
      return { ...o, types: types };
    })
    .filter(o => o.types >= 2)
    .sort(
      (a, b) =>
        b.types - a.types ||
        b.single.length - a.single.length ||
        b.capacity.length - a.capacity.length
    );

  const accounts = new Map<
    string,
    {
      id: string;
      name: string;
      types: Set<string>;
      keyContacts: Set<string>;
      counts: Record<RiskType, number>;
      owners: Map<string, { name: string; types: Set<string> }>;
    }
  >();
  items.forEach(function (i) {
    if (!i.account_id) return;
    if (!accounts.has(i.account_id)) {
      accounts.set(i.account_id, {
        id: i.account_id,
        name: i.account_name,
        types: new Set(),
        keyContacts: new Set(),
        counts: { "single-owner": 0, capacity: 0, "fan-in": 0 },
        owners: new Map(),
      });
    }
    const a = accounts.get(i.account_id)!;
    a.types.add(i.riskType);
    a.counts[i.riskType] += 1;
    if (i.isKey && i.contact_id) a.keyContacts.add(i.contact_id);
    const people =
      i.riskType === "fan-in"
        ? (i.current_owner_ids || []).map(function (id, idx) {
            return {
              id: id,
              name: (i.current_owner_names || [])[idx] || "Owner",
            };
          })
        : i.owner_id
          ? [{ id: i.owner_id, name: i.owner_name || "Owner" }]
          : [];
    people.forEach(function (person) {
      if (!person.id) return;
      const existing = a.owners.get(person.id) || {
        name: person.name,
        types: new Set<string>(),
      };
      existing.types.add(i.riskType);
      a.owners.set(person.id, existing);
    });
  });
  const multi = Array.from(accounts.values())
    .filter(a => a.types.size >= 2)
    .map(function (a) {
      const shared = Array.from(a.owners.values()).filter(function (owner) {
        return owner.types.size === a.types.size;
      });
      const commonOwner =
        shared.length === 1
          ? shared[0].name
          : shared.length
            ? shared.length + " owners"
            : "—";
      return {
        id: a.id,
        name: a.name,
        types: a.types,
        keyContacts: a.keyContacts.size,
        counts: a.counts,
        commonOwner: commonOwner,
      };
    })
    .sort(
      (a, b) =>
        b.types.size - a.types.size ||
        b.keyContacts - a.keyContacts ||
        a.name.localeCompare(b.name)
    );

  return {
    single: single,
    capacity: capacity,
    fan: fan,
    patterns: [single, capacity, fan].filter(l => l.length).length,
    ownerLine: ownerLine,
    multi: multi,
  };
}
